import { useCallback, useContext, useEffect } from 'react';
import {
  ChannelTypeUnits,
  ChannelTypeUnitsAndPercentageModel,
  DataOptionsType,
  GraphUnitsModel,
  IntervalOptionsType,
} from '@shared/models';
import { GraphActionType, GraphContext } from '@core/context';
import { isEmpty } from 'lodash';

export const useGraphFilterState = () => {
  const { state: graphState, dispatch: graphDispatch } = useContext(GraphContext);
  const {
    channelTypeUnitsAndPercentage: { channelTypeUnitOptions, canShowPercentage },
  } = graphState;

  const setIntervalFilter = useCallback(
    (payload: IntervalOptionsType) => {
      graphDispatch({ type: GraphActionType.SetInterval, payload });
    },
    [graphDispatch]
  );

  const setDataTypeFilter = useCallback(
    (payload: DataOptionsType) => {
      graphDispatch({ type: GraphActionType.SetDataType, payload });
    },
    [graphDispatch]
  );

  const setUnitsFilter = useCallback(
    (payload: GraphUnitsModel[]) => {
      graphDispatch({ type: GraphActionType.SetUnits, payload });
    },
    [graphDispatch]
  );

  const setShowAsPercentageFilter = useCallback(
    (payload: boolean) => {
      graphDispatch({ type: GraphActionType.SetShowAsPercentage, payload });
    },
    [graphDispatch]
  );

  const setChannelTypeUnitsAndPercentage = useCallback(
    (payload: ChannelTypeUnitsAndPercentageModel) => {
      if (payload && payload?.channelTypeUnitOptions) {
        graphDispatch({
          type: GraphActionType.SetChannelTypeUnitsAndPercentage,
          payload,
        });
      }
    },
    [graphDispatch]
  );

  const mapUnitsFromResponseToContextState = useCallback(
    (channelTypeUnitOpt: ChannelTypeUnits[]) =>
      channelTypeUnitOpt?.map(({ type, units, intervals, intervalMode }) => ({
        type,
        unit: units.find((unit) => unit.isDefault === true)?.name ?? null,
        interval: intervals?.find((interval) => interval.isDefault === true)?.interval ?? null,
        intervalMode,
      })),
    []
  );

  useEffect(() => {
    if (!channelTypeUnitOptions || isEmpty(channelTypeUnitOptions)) {
      setUnitsFilter([]);
    }
  }, [channelTypeUnitOptions, setUnitsFilter]);

  useEffect(() => {
    // init defaults for showAsPercentage
    setShowAsPercentageFilter(canShowPercentage ? false : null);
  }, [canShowPercentage, setShowAsPercentageFilter]);

  return {
    graphState,
    setIntervalFilter,
    setDataTypeFilter,
    setUnitsFilter,
    mapUnitsFromResponseToContextState,
    setShowAsPercentageFilter,
    setChannelTypeUnitsAndPercentage,
  };
};
